// System
import { useMemo } from 'react';
import { Box } from '@mui/material';
import { parseISO } from 'date-fns';
import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';

// Components
import { TableColumnHeader } from '@Components/shared/tables/columns';
import { useEllipsis } from '@Components/shared/styles/useEllipsis';
import { DateLabel } from '@Components/shared/labels/DateLabel';
import { numberFormatter } from '@Features/analytics/AnalyticsList/useAnalyticsTableColumns';

// Contracts
import {
  TableColumnDescription,
  TableColumnDescriptionArray,
} from '@contracts/view/TableColumnDescription';
import { Filterable } from '@contracts/common/traits/Filterable';
import { ProviderIgnoredRequestsItemDto } from '@contracts/api/dto/ProviderIgnoredRequests/ProviderIgnoredRequestsDto';

// Utils
import {
  ProviderIgnoredRequestsListColumns,
  ProviderIgnoredRequestsListFilter,
  providerIgnoredRequestsListColumns,
} from '@utils/tables/providerIgnoredRequests/providerIgnoredRequestsListFilter';

// Features
import TruncateText from '@Components/truncate-text/TruncateText';

type HeaderOptions = {
  filter: Filterable<ProviderIgnoredRequestsListFilter>;
};

type Columns = TableColumnDescriptionArray<
  ProviderIgnoredRequestsItemDto,
  HeaderOptions,
  typeof providerIgnoredRequestsListColumns
>;

type Column = TableColumnDescription<
  ProviderIgnoredRequestsItemDto,
  HeaderOptions,
  ProviderIgnoredRequestsListColumns
>;

const useSx = () => {
  return {
    id: { width: '80px' },
  } satisfies Record<string, SystemStyleObject<Theme>>;
};

export const useProviderIgnoredRequestsTableColumns = () => {
  const ellipsis = useEllipsis();
  const styles = useSx();

  return useMemo(
    (): Column[] =>
      [
        {
          name: 'providerIgnoredRequests.startDate',
          title: 'Start Date',
          sx: { minWidth: '140px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.startDate && (
                <DateLabel date={new Date(parseISO(row?.startDate))} format="long" />
              )}
            </Box>
          ),
        },
        {
          name: 'providerIgnoredRequests.endDate',
          title: 'End Date',
          sx: { minWidth: '140px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.endDate && <DateLabel date={new Date(parseISO(row?.endDate))} format="long" />}
            </Box>
          ),
        },
        {
          name: 'providerIgnoredRequests.betId',
          title: 'Bet ID',
          sx: { minWidth: '170px', maxWidth: '180px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>
              {row?.betId && (
                <TruncateText text={row?.betId} maxLength={8} textStyles={styles.id} />
              )}
            </Box>
          ),
        },
        {
          name: 'providerIgnoredRequests.amount',
          title: 'Bet Amount',
          sx: { minWidth: '180px', maxWidth: '240px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box sx={{ ...ellipsis }}>{row?.amount && numberFormatter(Number(row?.amount))}</Box>
          ),
        },
      ] satisfies Columns,
    [ellipsis]
  );
};
